/**
 * @module components/CauseCard
 * @description Brief §4.4 cause card: image, category tag, partner, title,
 * description, progress (raised/goal, donors, days left), Quick View,
 * Add to Cart (prominent, with cart icon), Share, Donate, Save.
 * `layout` switches between grid card and list row (brief §4.3).
 */
import type { CSSProperties, JSX } from "react";
import { useGiving } from "../app/GivingContext";
import { useCauseActions } from "../app/useCauseActions";
import { CATEGORY_LABELS, PARISH_LABELS, URGENCY_LABELS } from "../config/taxonomy";
import type { Cause } from "../domain/types";
import { Icon } from "./Icons";
import { Progress } from "./Progress";
import "./CauseCard.css";

/** Props for <CauseCard>. */
interface CauseCardProps {
    cause: Cause;
    layout: "grid" | "list";
    /** Position in the results; drives the Tier 2 staggered entrance (motion.css). */
    index?: number;
}

/** Urgency badge; "ongoing" shows none (a badge on everything means nothing). */
export function UrgencyBadge({ cause }: { cause: Cause }): JSX.Element | null {
    if (cause.urgency === "ongoing") return null;
    return <span className={`badge ${cause.urgency === "urgent" ? "badge--urgent" : "badge--time"}`}>{URGENCY_LABELS[cause.urgency]}</span>;
}

/** One cause card. */
export function CauseCard({ cause, layout, index = 0 }: CauseCardProps): JSX.Element {
    const { openQuickView } = useGiving();
    const actions = useCauseActions();
    const saved: boolean = actions.isSaved(cause);
    const inCart: boolean = actions.isInCart(cause);

    return (
        <article className={`cause-card cause-card--${layout}`} aria-labelledby={`cause-${cause.id}`}
            style={{ "--stagger-i": index % 6 } as CSSProperties}>
            <div className="cause-card__media">
                <button type="button" className="cause-card__image-btn" onClick={() => openQuickView(cause.id)} aria-label={`Quick view: ${cause.title}`}>
                    <img src={cause.image.src} alt={cause.image.alt} loading="lazy" />
                </button>
                <span className="cause-card__category">{CATEGORY_LABELS[cause.category]}</span>
                <button type="button" className="icon-btn cause-card__save" aria-pressed={saved} onClick={() => actions.toggleSaved(cause)}
                    aria-label={saved ? `Remove ${cause.title} from saved` : `Save ${cause.title}`}>
                    <Icon name={saved ? "heart-filled" : "heart"} size={18} />
                </button>
                {cause.image.isIllustrative && <span className="cause-card__illustrative">Illustrative image</span>}
            </div>

            <div className="cause-card__body">
                <div className="cause-card__eyebrow">
                    <span className="cause-card__partner">{cause.partner}</span>
                    <UrgencyBadge cause={cause} />
                </div>
                <h3 id={`cause-${cause.id}`} className="cause-card__title">{cause.title}</h3>
                <p className="cause-card__summary">{cause.summary}</p>
                <p className="cause-card__place">{PARISH_LABELS[cause.parish]} · {cause.tags.slice(0, 2).join(" · ")}</p>
                <Progress funding={cause.funding} />

                <div className="cause-card__actions">
                    <button type="button" className={`btn btn--primary cause-card__add${inCart ? " is-in-cart" : ""}`} onClick={() => actions.addToCart(cause)}>
                        <Icon name={inCart ? "check" : "cart"} /> {inCart ? "In cart · update" : "Add to Cart"}
                    </button>
                    <button type="button" className="btn btn--ghost" onClick={() => actions.donateNow(cause)}>Donate</button>
                    <div className="cause-card__minor">
                        <button type="button" className="icon-btn" onClick={() => openQuickView(cause.id)} aria-label={`Quick view: ${cause.title}`}><Icon name="eye" size={18} /></button>
                        <button type="button" className="icon-btn" onClick={() => void actions.share(cause)} aria-label={`Share ${cause.title}`}><Icon name="share" size={18} /></button>
                    </div>
                </div>
            </div>
        </article>
    );
}
